/**
 * 86exw61rq - Bikram Sambat (BS) calendar conversion + formatting.
 *
 * Backed by the maintained `nepali-date-converter` package, whose calendar
 * data is the authoritative Nepali patro (cross-checked against the Nepal
 * Rajpatra / IRD). This module is a thin adapter that keeps the existing
 * public surface unchanged so call sites don't move:
 * - months are 1..12 here (the library is 0-indexed internally),
 * - AD values are UTC-midnight `Date`s (callers read `getUTC*`),
 * - out-of-range input returns null so callers fall back to AD-only display.
 *
 * Supported range: BS 2000 (1943 AD) through BS 2090 (2033 AD), the span of
 * the library's table. This replaced a hand-maintained lookup table whose
 * month lengths drifted ~20 days by the 2080s (BS months mapped to the wrong
 * AD range in the IRD VAT return). The library removes that whole class of
 * data-entry bug; the regression suite in `bsDate.test.ts` pins the anchors.
 *
 * Public surface:
 * - `adToBs(date)` → `{ year, month, day }` (month 1..12, day 1..32)
 * - `bsToAd({ year, month, day })` → Date (AD, UTC midnight)
 * - `daysInBsMonth(year, month)` → number of days (0 when out of range)
 * - `bsYearRange()` → `{ first, last }` supported BS years
 * - `formatBs(date)` - "21 Mangsir 2082" / Devanagari variant via `style`
 * - `formatBsWithAd(date)` - "21 Mangsir 2082 (6 Dec 2025)"
 * - `formatIsoToBs(iso)` - ISO AD string → BS string
 * - `BS_MONTHS_EN` / `BS_MONTHS_NP` - month name tables
 * - `BS_WEEKDAYS_NP` - Devanagari weekday names indexed by `date.getDay()`
 */

import NepaliDate, { dateConfigMap } from 'nepali-date-converter'
import { getHostDateLocale } from './format'

// One spelling across the app, matching the backend's period and tax-period
// labels (api/pms_finance/nepali_date.py BS_MONTH_NAMES), so a screen never
// says "Asoj 2083" beside a server label "Ashwin 2083".
export const BS_MONTHS_EN: readonly string[] = [
  'Baishakh',
  'Jestha',
  'Ashadh',
  'Shrawan',
  'Bhadra',
  'Ashwin',
  'Kartik',
  'Mangsir',
  'Poush',
  'Magh',
  'Falgun',
  'Chaitra',
]

export const BS_MONTHS_NP: readonly string[] = [
  'बैशाख',
  'जेठ',
  'असार',
  'श्रावण',
  'भदौ',
  'असोज',
  'कार्तिक',
  'मंसिर',
  'पुष',
  'माघ',
  'फाल्गुन',
  'चैत्र',
]

export const BS_WEEKDAYS_NP: readonly string[] = [
  'आइतबार',
  'सोमबार',
  'मङ्गलबार',
  'बुधबार',
  'बिहीबार',
  'शुक्रबार',
  'शनिबार',
]

export interface BsDate {
  year: number
  month: number // 1..12
  day: number // 1..32
}

// `dateConfigMap` keys the month lengths by name, in calendar order. Kept
// separate from BS_MONTHS_EN because the library spells some months its own
// way ("Baisakh", "Asar", "Aswin").
const BS_CONFIG_KEYS = [
  'Baisakh',
  'Jestha',
  'Asar',
  'Shrawan',
  'Bhadra',
  'Aswin',
  'Kartik',
  'Mangsir',
  'Poush',
  'Magh',
  'Falgun',
  'Chaitra',
] as const

const BS_YEARS = Object.keys(dateConfigMap).map(Number)
const BS_FIRST_YEAR = Math.min(...BS_YEARS)
const BS_LAST_YEAR = Math.max(...BS_YEARS)

/**
 * Convert an AD `Date` to BS components. Returns null when out of the
 * library's table range (before BS 2000 / after BS 2090).
 */
export function adToBs(date: Date): BsDate | null {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null
  try {
    // Preserve the historical contract: key off the date's UTC calendar day.
    // Anchor at UTC noon so the Kathmandu offset the library applies can never
    // tip the conversion into an adjacent day.
    const utcNoon = new Date(
      Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 12),
    )
    const bs = NepaliDate.fromAD(utcNoon).getBS()
    const result = { year: bs.year, month: bs.month + 1, day: bs.date }
    // Outside its table the library does not throw: it wraps round to a
    // plausible but wrong BS date (1900 AD comes back as 2043). Keep the
    // result only when it converts back to the same AD day.
    const back = bsToAd(result)
    if (
      !back ||
      back.getTime() !== Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
    ) {
      return null
    }
    return result
  } catch {
    return null
  }
}

/**
 * Convert BS components to an AD `Date` (midnight UTC). Returns null when the
 * requested year isn't representable in the table.
 */
export function bsToAd(bs: BsDate): Date | null {
  const { year, month, day } = bs
  if (year < BS_FIRST_YEAR || year > BS_LAST_YEAR) return null
  if (month < 1 || month > 12 || day < 1 || day > 32) return null
  try {
    // getAD() returns a 0-indexed month, which is exactly what Date.UTC wants.
    const ad = new NepaliDate(year, month - 1, day).getAD()
    return new Date(Date.UTC(ad.year, ad.month, ad.date))
  } catch {
    return null
  }
}

/** Days in a BS month (1..12). Returns 0 when out of range. */
export function daysInBsMonth(year: number, month: number): number {
  if (year < BS_FIRST_YEAR || year > BS_LAST_YEAR) return 0
  if (month < 1 || month > 12) return 0
  const row = dateConfigMap[String(year)]
  return row ? row[BS_CONFIG_KEYS[month - 1]] : 0
}

/** Inclusive range of BS years the converter supports. */
export function bsYearRange(): { first: number; last: number } {
  return { first: BS_FIRST_YEAR, last: BS_LAST_YEAR }
}

/**
 * "21 Mangsir 2082" (default) or "२१ मंसिर २०८२" when `style="devanagari"`.
 */
export function formatBs(date: Date, style: 'en' | 'devanagari' = 'en'): string {
  const bs = adToBs(date)
  if (!bs) return ''
  const day = style === 'devanagari' ? _toDevanagari(bs.day) : String(bs.day)
  const month = (style === 'devanagari' ? BS_MONTHS_NP : BS_MONTHS_EN)[bs.month - 1]
  const year = style === 'devanagari' ? _toDevanagari(bs.year) : String(bs.year)
  return `${day} ${month} ${year}`
}

/**
 * "21 Mangsir 2082 (6 Dec 2025)" - BS as primary, AD in parentheses.
 */
export function formatBsWithAd(date: Date, style: 'en' | 'devanagari' = 'en'): string {
  const bs = formatBs(date, style)
  if (!bs) return ''
  const ad = date.toLocaleDateString(getHostDateLocale(), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
  return `${bs} (${ad})`
}

/**
 * Parse an ISO date string (`YYYY-MM-DD`) to a BS string. Convenience
 * wrapper so callers can pipe API date columns straight to BS.
 */
export function formatIsoToBs(
  iso: string | null | undefined,
  style: 'en' | 'devanagari' = 'en',
): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  return formatBs(d, style)
}

// Helper: AD integer to Devanagari numerals.
const DEVANAGARI_DIGITS = '०१२३४५६७८९'
function _toDevanagari(n: number): string {
  return String(n)
    .split('')
    .map((c) => (c >= '0' && c <= '9' ? DEVANAGARI_DIGITS[Number(c)] : c))
    .join('')
}
